import { useEffect } from 'react';
import { useTenant } from '../contexts/TenantContext';

interface PageHeadProps {
  title: string;
  description: string;
  canonicalPath?: string;
}

/**
 * Updates <title> + <meta> + <link rel=canonical> on each route change.
 *
 * In white-label mode (tenant context present), canonical URLs use the
 * tenant's help origin (e.g. https://help.dansdiscounttools.com) rather than
 * help.auctisync.com so search engines index the tenant's branded surface.
 */
export function PageHead({ title, description, canonicalPath }: PageHeadProps) {
  const tenant = useTenant();
  const helpOrigin = tenant?.help_origin ?? 'https://help.auctisync.com';

  useEffect(() => {
    document.title = title;
    setMeta('description', description);
    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    if (canonicalPath) {
      setMeta('og:url', `${helpOrigin}${canonicalPath}`, true);
      setLink('canonical', `${helpOrigin}${canonicalPath}`);
    }
  }, [title, description, canonicalPath, helpOrigin]);
  return null;
}

function setMeta(key: string, value: string, isProperty = false): void {
  const attr = isProperty ? 'property' : 'name';
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
}

function setLink(rel: string, href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}
